import { collection, query, where, orderBy, limit as limitQuery, onSnapshot, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from './firebaseCore';
import Decimal from 'decimal.js';
import { fetchEurRate } from '../services/PriceService';

export const subscribeToUserBalance = (
    userEmail: string,
    onUpdate: (balanceData: { ethBalance: number; eurBalance: number }) => void,
    onError?: (error: Error) => void
  ): (() => void) => {
    if (!userEmail) {
      console.error("No userEmail provided");
      return () => {};
    }
  
    // Create reference to the user's balance collection
    const balanceCollectionRef = collection(db, 'user_balance');
    const userDocRef = doc(db, 'users', userEmail);
    
    // Create query to get the latest balance entry
    const q = query(
      balanceCollectionRef,
      where('user', '==', userDocRef),
      orderBy('TransxnDateTime', 'desc'),
      limitQuery(1)
    );
  
    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        if (!querySnapshot.empty) {
          const latestDoc = querySnapshot.docs[0];
          const data = latestDoc.data();
          const ethBalance = data.currentBalance || 0;
  
          try {
            const eurRate = await fetchEurRate();
            if (!eurRate) {
              throw new Error('EUR rate unavailable');
            }
            const eurBalance = ethBalance * eurRate;
  
            onUpdate({
              ethBalance,
              eurBalance
            });
          } catch (error) {
            console.error("Error fetching ETH price:", error);
            // Still update with ETH balance even if price conversion fails
            onUpdate({
              ethBalance,
              eurBalance: 0
            });
          }
        } else {
          onUpdate({
            ethBalance: 0,
            eurBalance: 0
          });
        }
      },
      (error) => {
        console.error("Balance subscription error:", {
          error,
          userEmail
        });
        if (onError) onError(error);
      }
    );
  
    return unsubscribe;
  };

  
export const calculateWithdrawableBalance = async (userEmail: string): Promise<number> => {
    if (!userEmail) {
      console.error("No userEmail provided");
      return 0;
    }
  
    try {
      const userDocRef = doc(db, 'users', userEmail);
      
      // 1. Get user's free bet amount
      const userDoc = await getDoc(userDocRef);
      const freeBetRedeemed = new Decimal(userDoc.data()?.freeBetRedeemed?.toString() || "0");
      const pendingWithdrawAmount = new Decimal(userDoc.data()?.pendingWithdrawAmount?.toString() || "0");

      // 2. Get current balance
      const balanceRef = collection(db, 'user_balance');
      const balanceQuery = query(
        balanceRef,
        where('user', '==', userDocRef),
        orderBy('TransxnDateTime', 'desc'),
        limitQuery(1)
      );
  
      const balanceSnapshot = await getDocs(balanceQuery);
      let currentBalance = new Decimal("0");
      if (!balanceSnapshot.empty) {
        currentBalance = new Decimal(balanceSnapshot.docs[0].data().currentBalance?.toString() || "0");
      }

      // 3. Calculate locked deposits
      const transactionsRef = collection(db, 'user_transactions');
      const recentDepositsQuery = query(
        transactionsRef,
        where('user', '==', userDocRef),
        where('deposit_withdraw', '==', true)
      );
  
      const depositDocs = await getDocs(recentDepositsQuery);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
      let lockedDepositsTotal = new Decimal("0");
      depositDocs.forEach(doc => {
        const data = doc.data();
        const transactionDate = data.TransxnDateTime.toDate();
        
        if (!data.free_bet && transactionDate >= sevenDaysAgo) {
          lockedDepositsTotal = lockedDepositsTotal.plus(
            new Decimal(data.amountEth?.toString() || "0")
          );
        }
      });

      // 4. Calculate total withdrawable amount
      const totalWithdrawable = currentBalance
        .minus(lockedDepositsTotal)
        .minus(freeBetRedeemed)
        .minus(pendingWithdrawAmount);

      return Math.max(0, Number(totalWithdrawable.toFixed(6)));
  
    } catch (error) {
      console.error("Error calculating withdrawable balance:", error);
      return 0;
    }
  };


export const subscribeToWithdrawableBalance = (
    userEmail: string,
    onUpdate: (balance: number) => void,
    onError?: (error: Error) => void
  ): (() => void) => {
    if (!userEmail) {
      onUpdate(0);
      return () => {};
    }
  
    const userDocRef = doc(db, 'users', userEmail);
    
    const calculate = async () => {
      try {
        const balance = await calculateWithdrawableBalance(userEmail);
        onUpdate(balance);
      } catch (error) {
        console.error("Error in balance subscription:", error);
        if (onError) onError(error as Error);
      }
    };
  
    // Set up listeners for all relevant collections
    const unsubscribeUser = onSnapshot(userDocRef, calculate);
    
    // Listen to balance changes
    const balanceRef = collection(db, 'user_balance');
    const balanceQuery = query(
      balanceRef,
      where('user', '==', userDocRef),
      orderBy('TransxnDateTime', 'desc'),
      limitQuery(1)
    );
    const unsubscribeBalance = onSnapshot(balanceQuery, calculate);
  
    // Listen to transactions for new deposits/withdrawals
    const transactionsRef = collection(db, 'user_transactions');
    const transactionsQuery = query(
      transactionsRef,
      where('user', '==', userDocRef)
    );
    const unsubscribeTransactions = onSnapshot(transactionsQuery, calculate);
  
    // Return cleanup function
    return () => {
      unsubscribeUser();
      unsubscribeBalance();
      unsubscribeTransactions();
    };
  };


// export const subscribeToEnterDrawStatus = (
//     userEmail: string,
//     onUpdate: (canEnterDraw: boolean) => void,
//     onError?: (error: Error) => void
//   ): (() => void) => {
//     if (!userEmail) {
//       onUpdate(false);
//       return () => {};
//     }
  
//     const userDocRef = doc(db, 'users', userEmail);
    
//     const unsubscribe = onSnapshot(
//       userDocRef,
//       (docSnapshot) => {
//         if (docSnapshot.exists()) {
//           const userData = docSnapshot.data();
//           onUpdate(userData.enter_draw === true); // Explicitly check for true
//         } else {
//           onUpdate(false);
//         }
//       },
//       (error) => {
//         console.error("Error in enter draw status subscription:", error);
//         if (onError) onError(error);
//         onUpdate(false);
//       }
//     );
  
//     return unsubscribe;
//   };